import React, { useEffect, useState, useRef } from 'react';
import {
    Image, Text, Flex, Box
} from 'components/ui';
import Flag from 'components/Flag/Flag';
import Chiclet from 'components/Chiclet';
import FrameworkUtils from 'utils/framework';
import helpersUtils from 'utils/Helpers';
import {
    space, radii, mediaQueries, breakpoints, forms, gradients, colors
} from 'style/config';
import { DebouncedResize } from 'constants/events';
import MessageInputBar from 'ai/components/SuperChat/MessageInputBar/MessageInputBar';
import { CATEGORIES } from 'ai/constants/categories';
import Location from 'utils/Location';
import { AI_CHAT_IMAGE, AI_CHAT_BLACK_IMAGE } from 'ai/utils/imagePaths';
import { trackSuperChatPromptClick, getSuperChatPageContext } from 'ai/utils/trackSuperChatPromptClick';
import { trackLandingPLP, useViewableImpression } from 'ai/analytics';

const { wrapFunctionalComponent } = FrameworkUtils;
const { deferTaskExecution } = helpersUtils;

function ProductListPage(props) {
    const {
        localization,
        fetchEntryPointPromptsPlp,
        nthCategory,
        handlePromptClick,
        products,
        chatName,
        searchKeyword,
        clientId,
        anonymousId,
        isRequestInFlight,
        updatePrompts,
        isGenAINewUIEnabled
    } = props;

    const { aiChatInputPlaceholderPlp, aiChatInputPlaceholderPlpLgUi, lookingForAGift } = localization;
    const [prompts, setPrompts] = useState([]);
    const [isMobile, setIsMobile] = useState(false);
    const componentRef = useRef(null);

    useEffect(() => {
        const fetchPrompts = async () => {
            const res = await fetchEntryPointPromptsPlp(nthCategory, searchKeyword, clientId, anonymousId);
            setPrompts(res?.entrypoint_prompts || []);
            updatePrompts(res?.entrypoint_prompts || []);
        };

        fetchPrompts();
        checkIsMobile();
        window.addEventListener(DebouncedResize, checkIsMobile);

        return () => {
            window.removeEventListener(DebouncedResize, checkIsMobile);
        };
    }, [nthCategory?.categories?.[0]?.categoryId, searchKeyword]);

    const checkIsMobile = () => {
        setIsMobile(window.matchMedia(breakpoints.smMax).matches);
    };

    // Determine page type and build SOT fields accordingly
    const isSearchPage = Location.isSearchPage();
    const sotFields = isSearchPage
        ? {
            // Search page tracking
            sotV104: searchKeyword || ''
        }
        : {
            // Category page tracking
            sotV163: nthCategory?.displayName || '',
            sotV08: nthCategory?.categoryId || ''
        };

    // Track viewable impression for Category/Search Page
    useViewableImpression({
        componentRef,
        prompts,
        sotFields,
        dependencies: [nthCategory, searchKeyword]
    });

    const openAIChat = (prompt = null) => {
        if (isRequestInFlight) {
            return;
        }

        deferTaskExecution(() => {
            if (prompt) {
                const promptText = prompt.toLowerCase();
                trackLandingPLP(promptText);
                const pageContext = getSuperChatPageContext({
                    nthCategory,
                    searchKeyword
                });
                trackSuperChatPromptClick({
                    promptText: prompt,
                    pageContext
                });
            } else {
                trackLandingPLP();
            }

            const isGiftFinderCategory = nthCategory?.categoryId === CATEGORIES.GIFT;
            const selectedPrompt = prompt || (isGiftFinderCategory ? lookingForAGift : null);

            handlePromptClick(prompts, nthCategory, products, selectedPrompt, searchKeyword);
        });
    };

    return (
        <Box
            ref={componentRef}
            backgroundColor='lightBlue'
            padding={space[4]}
            css={[styles.box, isGenAINewUIEnabled && styles.boxControl]}
            borderRadius={radii[1]}
        >
            <Flex
                flexDirection='column'
                gap={`${space[4]}px`}
                css={styles.productAdvisorTitleRow}
            >
                <Image
                    disableLazyLoad={true}
                    alt='AI Chat'
                    src={isGenAINewUIEnabled ? AI_CHAT_BLACK_IMAGE : AI_CHAT_IMAGE}
                    width={60}
                    height={60}
                    css={styles.chatImg}
                />
                <Text
                    is='h2'
                    fontSize={['md', 'lg']}
                    lineHeight='tight'
                    fontWeight='bold'
                >
                    {chatName}
                    <Flag
                        backgroundColor={'black'}
                        children={localization.new}
                        marginLeft={space[3] - 2}
                        css={styles.flag}
                    />
                </Text>
            </Flex>
            <ul css={styles.suggestionList}>
                {Array.isArray(prompts) &&
                    prompts.length > 0 &&
                    prompts?.map(prompt => (
                        <li
                            key={`suggestion_${prompt}`}
                            css={[styles.suggestionListItem, isRequestInFlight && styles.disabled]}
                            onClick={() => openAIChat(prompt?.content)}
                        >
                            <Chiclet
                                variant='shadow'
                                children={prompt?.content}
                                width='100%'
                                css={[styles.chiclet, isRequestInFlight && styles.disabledChiclet]}
                            />
                        </li>
                    ))}
            </ul>
            <Box
                marginTop={space[3]}
                css={[styles.messageInputBar, isRequestInFlight && styles.disabled]}
                onClick={() => openAIChat()}
            >
                <MessageInputBar
                    placeholder={isMobile ? aiChatInputPlaceholderPlp : aiChatInputPlaceholderPlpLgUi}
                    disabled={true}
                />
            </Box>
        </Box>
    );
}

const styles = {
    box: {
        width: 275,
        height: 465,
        paddingTop: 68.5,
        paddingBottom: 68.5,
        paddingLeft: space[4],
        paddingRight: space[4],
        backgroundColor: colors.lightBlue,
        [mediaQueries.xxsMax]: {
            display: 'flex',
            flexDirection: 'column',
            width: '100vw',
            marginRight: -space[4],
            marginLeft: -space[2] - 1,
            padding: space[4],
            height: 'unset'
        }
    },
    boxControl: {
        backgroundColor: 'transparent',
        backgroundImage: gradients.aiBeautyChat
    },
    chatImg: {
        [mediaQueries.xxsMax]: {
            width: 24,
            height: 24
        }
    },
    productAdvisorTitleRow: {
        flex: '0 0 auto',
        [mediaQueries.xxsMax]: {
            order: 1,
            display: 'flex',
            flexDirection: 'row',
            gap: space[1],
            alignItems: 'center'
        }
    },
    flag: {
        [mediaQueries.xxsMax]: {
            marginBottom: space[1]
        }
    },
    suggestionList: {
        display: 'flex',
        gap: space[3],
        cursor: 'pointer',
        marginTop: space[5],
        flexDirection: 'column',
        overflowX: 'visible',
        overflowY: 'visible',
        paddingLeft: space[4],
        marginLeft: -space[4],
        [mediaQueries.xxsMax]: {
            order: 3,
            whiteSpace: 'nowrap',
            overflowX: 'auto',
            overflowY: 'visible',
            scrollbarWidth: 'none',
            marginRight: -space[4],
            display: 'line-item',
            '&::-webkit-scrollbar': { display: 'none' },
            paddingTop: space[4] + space[1],
            paddingBottom: space[2],
            marginTop: -space[2],
            marginBottom: -space[2]
        }
    },
    suggestionListItem: {
        [mediaQueries.xxsMax]: {
            display: 'inline-block',
            marginRight: space[1]
        }
    },
    messageInputBar: {
        width: '100%',
        [mediaQueries.xxsMax]: {
            order: 2
        }
    },
    chiclet: {
        paddingTop: space[2],
        paddingBottom: space[2]
    },
    disabled: {
        opacity: forms.DISABLED_OPACITY,
        pointerEvents: 'none',
        cursor: 'not-allowed'
    },
    disabledChiclet: {
        opacity: forms.DISABLED_OPACITY
    }
};

export default wrapFunctionalComponent(ProductListPage, 'ProductListPage');
